import { useEffect, useState, type FormEvent } from "react";
import { ArrowRight, ChevronDown } from "lucide-react";
import { CTAButton } from "#/components/shared/CTAButton";
import { BUSINESS, SERVICES } from "#/lib/constants";
import {
  HERO_ALT,
  HERO_DESKTOP,
  HERO_MOBILE,
  HERO_VIDEO,
  HERO_VIDEO_MOBILE,
} from "#/lib/images";
import { sendQuoteEmailAction, type QuoteFormData } from "#/lib/send-email";

/**
 * Hero — mirrored from the client's reference: full-bleed photo, centered
 * condensed uppercase headline, one accent button. The opaque nav bar covers
 * the photo's top strip, so the copy is pushed clear with var(--nav-h).
 * The quote card below keeps its desktop straddle onto the ValueProps band.
 */
export function Hero() {
  return (
    <section id="home" className="relative bg-graphite">
      {/* Photo + headline — full-height block, nothing overlaps it on mobile */}
      <div className="relative h-[92svh] min-h-[620px]">
        {/* Background photo (LCP — preloaded in index.tsx head) */}
        <div className="absolute inset-0 overflow-hidden">
          <picture>
            <source media="(max-width: 767px)" srcSet={HERO_MOBILE} />
            <source media="(min-width: 768px)" srcSet={HERO_DESKTOP} />
            <img
              src={HERO_DESKTOP}
              alt={HERO_ALT}
              className="h-full w-full object-cover"
              fetchPriority="high"
              decoding="async"
            />
          </picture>
          <HeroFilm />
          {/* Even scrim so centered white type reads on any crop */}
          <div className="absolute inset-0 bg-black/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-black/45" />
        </div>

        {/* Hero copy — centered, biased above the straddling quote card */}
        <div className="relative z-10 flex h-full flex-col items-center justify-center px-6 pt-[var(--nav-h)] pb-24 text-center lg:px-8 lg:pb-56">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-flame sm:text-sm">
            Residential &amp; Commercial Concrete Services
          </p>
          <h1 className="font-heading mt-5 font-stretch-[62.5%] text-5xl font-black uppercase leading-[0.88] text-white sm:text-7xl lg:text-[7rem] xl:text-[8rem]">
            <span className="block">Stronger from</span>
            <span className="block">the ground up</span>
          </h1>
          <div className="mt-9">
            <CTAButton
              href="#estimate"
              variant="flame"
              size="lg"
              className="rounded-none uppercase tracking-wide"
            >
              Get a Free Estimate
              <ArrowRight className="h-5 w-5" />
            </CTAButton>
          </div>
        </div>
      </div>

      {/* Quote-form card. Mobile/tablet: normal flow below the photo, on the
          flame band that continues into ValueProps. lg+: absolute straddle on
          the divider (unchanged desktop behavior). */}
      <div
        id="estimate"
        className="relative z-20 scroll-mt-24 bg-flame pt-8 lg:absolute lg:inset-x-0 lg:bottom-0 lg:translate-y-1/2 lg:bg-transparent lg:pt-0"
      >
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid gap-8 bg-graphite p-7 shadow-2xl shadow-black/40 ring-1 ring-white/5 md:grid-cols-5 md:p-10 lg:gap-12 lg:p-12">
            {/* Left column — trust line + offer */}
            <div className="flex flex-col md:col-span-2">
              <h2 className="font-heading text-2xl font-bold uppercase leading-tight text-white lg:text-3xl">
                Let our team earn your trust &amp; loyalty
              </h2>
              <ArrowRight className="mt-5 h-7 w-7 text-flame" strokeWidth={2.5} />
              <div className="mt-auto pt-10">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/60">
                  We Offer
                </p>
                <p className="font-heading mt-1 text-xl font-bold uppercase text-flame lg:text-2xl">
                  Free Same-Week Estimates
                </p>
              </div>
            </div>

            {/* Right column — quote form */}
            <div className="md:col-span-3">
              <QuoteForm />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * HeroFilm — the service film that plays behind the headline: one continuous
 * camera flight from a prepped slab, through a driveway pour, across a stamped
 * patio, out along a finished walkway.
 *
 * The still underneath stays the LCP element. This mounts nothing until the
 * page has finished loading, so the critical path is byte-for-byte what it was
 * before the film existed, and it only fades in once frames are actually
 * playing (never a black box waiting on the network). Reduced-motion and
 * data-saver visitors keep the photo and download no video at all.
 */
function HeroFilm() {
  const [src, setSrc] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const { connection } = navigator as Navigator & {
      connection?: { saveData?: boolean };
    };
    if (connection?.saveData) return;

    const load = () =>
      setSrc(
        window.matchMedia("(max-width: 767px)").matches
          ? HERO_VIDEO_MOBILE
          : HERO_VIDEO,
      );

    if (document.readyState === "complete") {
      load();
      return;
    }
    window.addEventListener("load", load, { once: true });
    return () => window.removeEventListener("load", load);
  }, []);

  if (!src) return null;

  return (
    <video
      src={src}
      autoPlay
      muted
      loop
      playsInline
      preload="auto"
      aria-hidden="true"
      tabIndex={-1}
      onPlaying={() => setPlaying(true)}
      className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-[1200ms] ${
        playing ? "opacity-100" : "opacity-0"
      }`}
    />
  );
}

const FIELD =
  "w-full rounded-md bg-white/[0.05] px-4 py-3.5 text-sm placeholder:text-concrete outline-none ring-1 ring-white/10 transition focus:ring-2 focus:ring-flame";

function QuoteForm() {
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">(
    "idle",
  );
  const [error, setError] = useState("");

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status === "sending") return;
    const form = e.currentTarget;
    const fd = new FormData(form);
    const zip = String(fd.get("zip") ?? "").trim();
    const payload: QuoteFormData = {
      name: `${fd.get("firstName") ?? ""} ${fd.get("lastName") ?? ""}`.trim(),
      email: String(fd.get("email") ?? "").trim(),
      phone: String(fd.get("phone") ?? "").trim(),
      service: (fd.get("service") as string) || undefined,
      message: zip ? `Zip code: ${zip}` : undefined,
      _hp: (fd.get("company") as string) || undefined,
    };

    setStatus("sending");
    setError("");
    try {
      const res = await sendQuoteEmailAction({ data: payload });
      if (res.success) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setError(res.error ?? "Something went wrong. Please try again.");
      }
    } catch {
      setStatus("error");
      setError(`An error occurred. Please call ${BUSINESS.phone}.`);
    }
  }

  if (status === "success") {
    return (
      <div className="flex h-full min-h-[16rem] flex-col justify-center">
        <p className="font-heading text-2xl font-bold uppercase text-white">
          Thank you!
        </p>
        <p className="mt-2 text-white/75">
          Your request is in. We&apos;ll reach out within one business day to
          schedule your free estimate.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="grid gap-4">
      {/* honeypot — hidden from users, catches bots */}
      <input
        type="text"
        name="company"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <input
          name="firstName"
          required
          placeholder="First name*"
          aria-label="First name"
          className={`${FIELD} text-white`}
        />
        <input
          name="lastName"
          required
          placeholder="Last name*"
          aria-label="Last name"
          className={`${FIELD} text-white`}
        />
        <input
          name="email"
          type="email"
          required
          placeholder="Email address*"
          aria-label="Email address"
          className={`${FIELD} text-white`}
        />
        <input
          name="phone"
          type="tel"
          required
          placeholder="Phone number*"
          aria-label="Phone number"
          className={`${FIELD} text-white`}
        />
        <input
          name="zip"
          inputMode="numeric"
          placeholder="Zip code"
          aria-label="Zip code"
          className={`${FIELD} text-white`}
        />
        <div className="relative">
          <select
            name="service"
            required
            defaultValue=""
            aria-label="Choose a service"
            className={`${FIELD} appearance-none pr-10 text-concrete valid:text-white`}
          >
            <option value="" disabled hidden>
              Choose a Service*
            </option>
            {SERVICES.map((s) => (
              <option key={s.slug} value={s.title} className="bg-charcoal text-white">
                {s.title}
              </option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-concrete" />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4 pt-1">
        <CTAButton
          type="submit"
          variant="flame"
          size="lg"
          className="uppercase tracking-wide"
          disabled={status === "sending"}
        >
          {status === "sending" ? "Sending…" : "Get a Free Quote"}
          <ArrowRight className="h-5 w-5" />
        </CTAButton>
        {status === "error" && <p className="text-sm text-red-300">{error}</p>}
      </div>
    </form>
  );
}
